// ADV-MKT-WATCHLIST-ADD-ACTION-001 — shared Active Watchlist actions.
// Reuses the SAME backend the WatchlistPanel uses (/api/v1/user/watchlist). Watchlist
// only — never an order/trade/broker path. Pure-ish helpers + a cross-component
// "watchlist changed" event so the Active Watchlist panel refreshes after a board add.

import { apiV1Base } from './apiConfig';

export const WATCHLIST_CHANGED_EVENT = 'adversyn:watchlist-changed';

export function dispatchWatchlistChanged(symbol?: string): void {
  try {
    window.dispatchEvent(new CustomEvent(WATCHLIST_CHANGED_EVENT, { detail: { symbol } }));
  } catch { /* ignore (non-browser) */ }
}

export function normalizeSymbol(symbol: string): string {
  return String(symbol || '').trim().toUpperCase();
}

export function isValidSymbol(symbol: string): boolean {
  return /^[A-Z0-9.\-]{1,10}$/.test(normalizeSymbol(symbol));
}

function _token(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('adversyn_auth_token');
}

/** GET the current watchlist symbols (uppercase). Returns [] on any failure. */
export async function fetchWatchlistSymbols(): Promise<string[]> {
  const token = _token();
  if (!token) return [];
  try {
    const r = await fetch(apiV1Base() + '/user/watchlist', {
      headers: { Authorization: `Bearer ${token}` }, credentials: 'omit',
    });
    if (!r.ok) return [];
    const body = await r.json();
    const data = body?.data ?? body;
    const syms = Array.isArray(data?.symbols) ? data.symbols : [];
    return syms.map((s: any) => normalizeSymbol(s?.symbol ?? s)).filter(Boolean);
  } catch {
    return [];
  }
}

/** POST a symbol to the Active Watchlist. Idempotent on the client (caller should
 *  guard duplicates); the same endpoint the WatchlistPanel "+ Add Symbol" uses. */
export async function addToWatchlist(symbol: string): Promise<{ ok: boolean; error?: string }> {
  const sym = normalizeSymbol(symbol);
  if (!isValidSymbol(sym)) return { ok: false, error: 'invalid symbol' };
  const token = _token();
  if (!token) return { ok: false, error: 'unauthenticated' };
  try {
    const r = await fetch(apiV1Base() + '/user/watchlist', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ symbol: sym }),
    });
    if (!r.ok) {
      let msg = `HTTP ${r.status}`;
      try { const b = await r.json(); msg = b?.error?.message || msg; } catch { /* ignore */ }
      return { ok: false, error: msg };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || e) };
  }
}
